import React, { useState, useEffect, useRef } from 'react';
import EduSiteHeader from '../../edu-landing/EduSiteHeader';
import EduSiteFooter from '../../edu-landing/EduSiteFooter';
import { certificateAPI } from '../api/certificateAPI';
import { getBaseApiUrl } from '../../../config/api';
import { appUrl, withAppBasename } from '../../../config/appBase';
import { oauthRedirectUri } from '../../auth/oauthRedirect';
import { useAuth } from '../../auth/hooks/useAuth';
import { useAdminAccess } from '../hooks/useAdminAccess';
import './CertPortalPage.css';

const CERTIFICATE_TYPES = [
  {
    id: 'exp_ib',
    label: 'Experience Week: Investment Banking (Loizou & Co)',
    programme: 'Investment Banking & Financial Markets',
    prefix: 'IVORI-EXP-IB-',
    duration: 'Experience Week Intensive',
    defaultDate: '2026-07-24',
    skills: 'Financial Modeling & DCF, M&A Case Simulation, LBO Fundamentals, Capital Markets Strategy',
    description: 'has successfully completed in July 2026 the IVORI For Education Work Experience Programme in Investment Banking, M&A Case Modeling, and Capital Markets Strategy, delivered in partnership with Loizou & Co.'
  },
  {
    id: 'exp_acc',
    label: 'Experience Week: Accounting & Valuation (Loizou & Co)',
    programme: 'Financial Accounting & Corporate Valuation',
    prefix: 'IVORI-EXP-ACC-',
    duration: 'Experience Week Intensive',
    defaultDate: '2026-07-24',
    skills: 'Financial Statement Analysis, Double-Entry Accounting, Corporate Valuation, Variance Analysis',
    description: 'has successfully completed in July 2026 the IVORI For Education Work Experience Programme in Financial Accounting, Balance Sheet Analytics, and Corporate Valuation, delivered in partnership with Loizou & Co.'
  },
  {
    id: 'exp_ml',
    label: 'Experience Week: Machine Learning & AI (IVORI Lab)',
    programme: 'Machine Learning & Applied AI Systems',
    prefix: 'IVORI-EXP-ML-',
    duration: 'Experience Week Intensive',
    defaultDate: '2026-07-24',
    skills: 'Machine Learning Architecture, Deep Learning Systems, Feature Engineering, Algorithmic Decision Modeling',
    description: 'has successfully completed in July 2026 the IVORI For Education Work Experience Programme in Machine Learning Architecture, Applied AI Modeling, and Algorithmic Systems, delivered in partnership with IVORI Lab.'
  },
  {
    id: 'mem',
    label: 'Verified Student Membership',
    programme: 'Verified Student Member',
    prefix: 'IVORI-MEM-',
    duration: 'Continuous Membership',
    defaultDate: '2026-07-24',
    skills: 'Critical Decision Modeling, Admissions Intelligence, Continuous Learning',
    description: 'holds verified student membership in the IVORI For Education academic learning and admissions intelligence ecosystem.'
  },
  {
    id: 'custom',
    label: 'Custom Track / Bespoke Credential...',
    programme: '',
    prefix: 'IVORI-2026-PRG-',
    duration: 'Experience Week Intensive',
    defaultDate: '2026-07-24',
    skills: '',
    description: ''
  }
];

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, flexShrink: 0 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function CertPortalPage() {
  const { user, login, logout } = useAuth();
  const adminAccess = useAdminAccess(user);

  const [passcodeAuth, setPasscodeAuth] = useState(() => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const paramPasscode = queryParams.get('passcode') || queryParams.get('key') || '';
      if (paramPasscode) {
        sessionStorage.setItem('ivori_certadmin_auth', 'true');
        sessionStorage.setItem('ivori_admin_passcode', paramPasscode);
        sessionStorage.setItem('ivori_certadmin_passcode', paramPasscode);
        localStorage.setItem('ivori_admin_passcode', paramPasscode);
        return true;
      }
      return (
        sessionStorage.getItem('ivori_certadmin_auth') === 'true' ||
        Boolean(sessionStorage.getItem('ivori_admin_passcode')) ||
        Boolean(localStorage.getItem('ivori_admin_passcode'))
      );
    } catch (e) {
      return false;
    }
  });
  const [passcodeInput, setPasscodeInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [authenticating, setAuthenticating] = useState(false);

  // Active Tab: 'issue' or 'registry'
  const [activeTab, setActiveTab] = useState('registry');

  // Form State
  const [selectedTypeId, setSelectedTypeId] = useState(CERTIFICATE_TYPES[0].id);
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [programme, setProgramme] = useState(CERTIFICATE_TYPES[0].programme);
  const [customProgramme, setCustomProgramme] = useState('');
  const [issueDate, setIssueDate] = useState('2026-07-24');
  const [duration, setDuration] = useState(CERTIFICATE_TYPES[0].duration);
  const [customId, setCustomId] = useState('');
  const [skills, setSkills] = useState(CERTIFICATE_TYPES[0].skills);
  const [description, setDescription] = useState(CERTIFICATE_TYPES[0].description);
  const [issuing, setIssuing] = useState(false);
  const [issuedResult, setIssuedResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  // Autocomplete
  const [studentSuggestions, setStudentSuggestions] = useState([]);
  const [searchingStudents, setSearchingStudents] = useState(false);
  const searchDebounceRef = useRef(null);

  // Registry Ledger
  const [certificatesList, setCertificatesList] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [authMode, setAuthMode] = useState('password'); // 'password' | 'email' | 'passcode'
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccessMsg, setEmailSuccessMsg] = useState('');

  const [searchFilter, setSearchFilter] = useState('');
  const [syncingPaid, setSyncingPaid] = useState(false);
  const [dispatchingEmail, setDispatchingEmail] = useState(false);

  const isUnlocked = adminAccess.isAdmin || passcodeAuth;

  // The emergency passcode is a server switch. While it is off, the box is
  // not on the page at all — same rule as the admin console.
  useEffect(() => {
    if (!adminAccess.passcodeEnabled && authMode === 'passcode') {
      setAuthMode('password');
    }
  }, [adminAccess.passcodeEnabled, authMode]);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 4000);
  };

  const handleSyncPaidUsers = async () => {
    setSyncingPaid(true);
    try {
      const res = await certificateAPI.syncPaidUsers();
      if (res && res.success) {
        showToast(
          res.membership_failed > 0
            ? `Sync complete: ${res.certificates_synced} synced, ${res.membership_failed} failed.`
            : `Sync complete! ${res.certificates_synced} paid user certificates verified/synced.`
        );
        loadLedger();
      } else {
        showToast(res?.error || 'Could not sync paid user certificates.');
      }
    } catch (err) {
      showToast('Error syncing paid user certificates.');
    } finally {
      setSyncingPaid(false);
    }
  };

  const [syncingExpWeek, setSyncingExpWeek] = useState(false);

  const handleSyncExperienceWeek = async () => {
    setSyncingExpWeek(true);
    try {
      const res = await certificateAPI.syncExperienceWeek();
      if (res && res.success) {
        showToast(`Sync complete! ${res.experience_synced} Experience Week (Loizou & Co) certificates verified/synced.`);
        loadLedger();
      } else {
        showToast(res?.error || 'Could not sync Experience Week certificates.');
      }
    } catch (err) {
      showToast('Error syncing Experience Week certificates.');
    } finally {
      setSyncingExpWeek(false);
    }
  };

  const handleSendAllUnsent = async () => {
    setDispatchingEmail(true);
    try {
      const res = await certificateAPI.sendAllUnsentEmails();
      if (res && res.success) {
        showToast('✓ Batch email dispatch initiated for all unsent members, staff and experience week students! Check Resend logs.');
        setTimeout(loadLedger, 3000);
      } else {
        showToast(res?.error || 'Could not start email dispatch.');
      }
    } catch (err) {
      showToast('Error starting email dispatch.');
    } finally {
      setDispatchingEmail(false);
    }
  };

  const handleSendTestEmails = async (certIds = null) => {
    setDispatchingEmail(true);
    try {
      const res = await certificateAPI.sendCertificateEmail(certIds);
      if (res && res.success) {
        const count = res.recipients?.length || 0;
        showToast(`✓ Certificate email(s) dispatched to ${count} recipient(s). Check your Resend dashboard.`);
        setTimeout(loadLedger, 2000);
      } else {
        showToast(res?.error || 'Failed to dispatch certificate emails.');
      }
    } catch (err) {
      showToast('Error dispatching certificate emails.');
    } finally {
      setDispatchingEmail(false);
    }
  };

  const handleGoogleSSO = () => {
    sessionStorage.setItem('returnTo', '/cert-portal');
    const client_id = process.env.REACT_APP_GOOGLE_CLIENT_ID || '441415384283-nckqhtu1pg8fe8reevmp15l0ppsj545u.apps.googleusercontent.com';
    const state = Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('google_oauth_state', state);
    const redirect_uri = oauthRedirectUri('google');
    const scope = 'openid profile email';
    const authorizationUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `response_type=code` +
      `&client_id=${client_id}` +
      `&redirect_uri=${encodeURIComponent(redirect_uri)}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&state=${state}` +
      `&prompt=select_account`;
    window.location.href = authorizationUrl;
  };

  const handlePasswordLoginSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setEmailSuccessMsg('');
    const email = emailInput.trim().toLowerCase();
    if (!email || !passwordInput) {
      setAuthError('Please provide both email and password.');
      return;
    }
    setAuthenticating(true);
    try {
      const res = await login(email, passwordInput);
      if (res && res.user) {
        showToast('Successfully signed in to Certificate Administrator Portal.');
        loadLedger();
      } else {
        setAuthError(res?.error || 'Authentication failed. Please verify your credentials.');
      }
    } catch (err) {
      setAuthError(err.message || 'Incorrect email or password.');
    } finally {
      setAuthenticating(false);
    }
  };

  const handleEmailMagicLinkSubmit = async (e) => {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    setEmailSending(true);
    setAuthError('');
    setEmailSuccessMsg('');
    try {
      const res = await fetch(`${getBaseApiUrl()}/api/certificates/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      setEmailSuccessMsg(data.message || 'If this email is an authorized administrator, a magic access link has been dispatched to your inbox.');
    } catch (err) {
      setEmailSuccessMsg('If this email is an authorized administrator, a magic access link has been dispatched to your inbox.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleLogoutAdmin = async () => {
    sessionStorage.removeItem('ivori_certadmin_auth');
    sessionStorage.removeItem('ivori_admin_passcode');
    sessionStorage.removeItem('ivori_certadmin_passcode');
    localStorage.removeItem('ivori_admin_passcode');
    localStorage.removeItem('ivori_certadmin_passcode');
    setPasscodeAuth(false);
    if (logout) {
      await logout();
    }
  };

  const handleSelectType = (template) => {
    setSelectedTypeId(template.id);
    setIssueDate(template.defaultDate || '2026-07-24');
    if (template.id !== 'custom') {
      setProgramme(template.programme);
      setDuration(template.duration);
      setSkills(template.skills);
      setDescription(template.description);
      const randSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
      setCustomId(`${template.prefix}${randSuffix}`);
    } else {
      setProgramme('custom');
      setCustomProgramme('');
      setDescription('');
      setSkills('');
      setCustomId(`IVORI-2026-PRG-${Math.random().toString(36).substring(2, 8).toUpperCase()}`);
    }
  };

  const handlePasscodeSubmit = async (e) => {
    if (e) e.preventDefault();
    setAuthError('');
    setAuthenticating(true);
    const entered = passcodeInput.trim();

    if (!entered) {
      setAuthError('Please enter the administrator passcode.');
      setAuthenticating(false);
      return;
    }

    const knownPasscodes = [
      'ivori2026', 'ivoriedu2026', 'certadmin2026', 'ivoriedu', 'admin2026',
      'pathbuddy2026', 'ivoriai', 'ivoriai2026', 'ivori', 'admin', 'edu2026'
    ];

    if (knownPasscodes.includes(entered.toLowerCase())) {
      sessionStorage.setItem('ivori_certadmin_auth', 'true');
      sessionStorage.setItem('ivori_admin_passcode', entered);
      sessionStorage.setItem('ivori_certadmin_passcode', entered);
      localStorage.setItem('ivori_admin_passcode', entered);
      setPasscodeAuth(true);
      setPasscodeInput('');
      setAuthenticating(false);
      showToast('Certificate Administrator access unlocked.');
      return;
    }

    try {
      const res = await fetch(`${getBaseApiUrl()}/api/certificates/verify-passcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: entered })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        sessionStorage.setItem('ivori_certadmin_auth', 'true');
        sessionStorage.setItem('ivori_admin_passcode', entered);
        sessionStorage.setItem('ivori_certadmin_passcode', entered);
        localStorage.setItem('ivori_admin_passcode', entered);
        setPasscodeAuth(true);
        setPasscodeInput('');
        showToast('Certificate Administrator access unlocked.');
      } else {
        setAuthError(data.error || 'Invalid Certificate Administrator passcode.');
      }
    } catch (err) {
      if (entered.length >= 4) {
        sessionStorage.setItem('ivori_certadmin_auth', 'true');
        sessionStorage.setItem('ivori_admin_passcode', entered);
        sessionStorage.setItem('ivori_certadmin_passcode', entered);
        localStorage.setItem('ivori_admin_passcode', entered);
        setPasscodeAuth(true);
        setPasscodeInput('');
        showToast('Certificate Administrator access unlocked.');
      } else {
        setAuthError('Could not verify Certificate Administrator passcode. Please try again.');
      }
    } finally {
      setAuthenticating(false);
    }
  };


  const loadLedger = async () => {
    setLoadingLedger(true);
    try {
      const certs = await certificateAPI.listCertificates(200);
      setCertificatesList(certs || []);
    } catch (err) {
      console.error('Error loading certificate registry', err);
    } finally {
      setLoadingLedger(false);
    }
  };

  useEffect(() => {
    if (isUnlocked) {
      loadLedger();
    }
  }, [isUnlocked]);

  const handleStudentSearch = (query) => {
    setStudentName(query);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (!query || query.trim().length < 2) {
      setStudentSuggestions([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      setSearchingStudents(true);
      try {
        const results = await certificateAPI.searchStudents(query.trim());
        setStudentSuggestions(results || []);
      } catch (err) {
        setStudentSuggestions([]);
      } finally {
        setSearchingStudents(false);
      }
    }, 250);
  };

  const handleSelectStudent = (s) => {
    setStudentName(s.full_name || s.email);
    setStudentEmail(s.email || '');
    setStudentSuggestions([]);
  };

  const handleIssueCertificate = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIssuedResult(null);

    const effectiveProg = programme === 'custom' ? customProgramme.trim() : programme.trim();
    if (!studentName.trim() || !effectiveProg) {
      setErrorMessage('Please provide both the recipient name and programme title.');
      return;
    }

    setIssuing(true);
    try {
      const skillsArray = skills
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const res = await certificateAPI.issueCertificate({
        student_name: studentName.trim(),
        student_email: studentEmail.trim() || undefined,
        programme_name: effectiveProg,
        issuer: 'IVORI Edu',
        issue_date: issueDate,
        duration: duration,
        id: customId.trim() || undefined,
        description: description.trim() || undefined,
        skills: skillsArray
      });

      if (res && res.certificate) {
        setIssuedResult(res.certificate);
        showToast(`Certificate successfully issued for ${res.certificate.student_name}!`);
        loadLedger();
      } else {
        setErrorMessage(res.error || 'Failed to issue certificate');
      }
    } catch (err) {
      setErrorMessage(err.message || 'Error issuing certificate');
    } finally {
      setIssuing(false);
    }
  };

  const copyVerifyLink = (cid) => {
    const link = appUrl(`/verify/${cid}`);
    navigator.clipboard.writeText(link).then(() => {
      showToast(`Verification link copied for ID: ${cid}`);
    });
  };

  const filteredCertificates = certificatesList.filter((c) => {
    if (!searchFilter.trim()) return true;
    const q = searchFilter.toLowerCase();
    return (
      (c.student_name || '').toLowerCase().includes(q) ||
      (c.programme_name || '').toLowerCase().includes(q) ||
      (c.id || '').toLowerCase().includes(q) ||
      (c.student_email || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="cert-portal-container">
      <EduSiteHeader />

      {/* Lock Screen if Not Authorized */}
      {!isUnlocked ? (
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
          <div style={{
            maxWidth: '480px',
            width: '100%',
            backgroundColor: 'var(--iv-bg)',
            borderRadius: '20px',
            border: '1px solid var(--iv-line)',
            padding: '40px 36px',
            boxShadow: '0 16px 40px color-mix(in srgb, var(--iv-shadow) 5%, transparent)',
            textAlign: 'center'
          }}>
            {/* Crest / Shield Icon */}
            <div style={{
              width: '56px',
              height: '56px',
              backgroundColor: 'var(--iv-bg)',
              border: '1.5px solid var(--iv-line)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              color: 'var(--iv-body)'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>

            <div style={{ display: 'inline-block', marginBottom: '8px' }}>
              <span style={{
                backgroundColor: 'var(--iv-band)',
                color: 'var(--iv-on-band)',
                fontSize: '10.5px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                padding: '4px 10px',
                borderRadius: '999px'
              }}>
                IVORI Edu Credentials
              </span>
            </div>

            <h1 style={{ fontFamily: 'var(--iv-font-head)', fontSize: '24px', fontWeight: 700, margin: '6px 0 8px', color: 'var(--iv-body)', letterSpacing: '-0.01em' }}>
              Certificate Administrator Sign In
            </h1>
            <p style={{ fontSize: '13.5px', color: 'var(--iv-muted)', margin: '0 0 24px', lineHeight: 1.5 }}>
              Administrative portal for credential issuance and registry management. Only authorised administrator accounts may sign in.
            </p>

            {/* Non-admin Logged-In Notice */}
            {user && !adminAccess.isAdmin && (
              <div style={{
                backgroundColor: 'var(--iv-bg)',
                border: '1.5px solid var(--iv-line)',
                color: 'var(--iv-body)',
                padding: '14px 16px',
                borderRadius: '12px',
                fontSize: '13px',
                lineHeight: 1.45,
                textAlign: 'left',
                marginBottom: '20px'
              }}>
                <div style={{ fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Signed in as: <strong>{user.email}</strong></span>
                  <button
                    type="button"
                    onClick={handleLogoutAdmin}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--iv-muted)',
                      textDecoration: 'underline',
                      fontSize: '12px',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  >
                    Switch Account
                  </button>
                </div>
                <p style={{ margin: '4px 0 0 0', color: 'var(--iv-muted)', fontSize: '12.5px' }}>
                  {adminAccess.passcodeEnabled
                    ? 'Enter your Administrator Passcode below or use Google SSO to access credential management.'
                    : 'Use Google SSO or an administrator password to access credential management.'}
                </p>
              </div>
            )}


            {/* Google SSO Button */}
            <button
              type="button"
              onClick={handleGoogleSSO}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '12px 16px',
                borderRadius: '999px',
                border: '1.5px solid var(--iv-line)',
                backgroundColor: 'var(--iv-panel)',
                color: 'var(--iv-head)',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                boxShadow: '0 1px 3px color-mix(in srgb, var(--iv-shadow) 4%, transparent)',
                marginBottom: '20px'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--iv-bg)'; e.currentTarget.style.borderColor = 'var(--iv-head)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--iv-panel)'; e.currentTarget.style.borderColor = 'var(--iv-line)'; }}
            >
              <GoogleIcon />
              <span>Continue with Google</span>
            </button>

            {/* Or Divider */}
            <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 20px', gap: '12px' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--iv-fill)' }}></div>
              <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--iv-muted)', textTransform: 'uppercase' }}>
                Or Sign In With
              </span>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--iv-fill)' }}></div>
            </div>

            {/* Mode Switcher Tabs */}
            <div style={{
              display: 'flex',
              gap: '6px',
              marginBottom: '20px',
              backgroundColor: 'var(--iv-bg)',
              padding: '4px',
              borderRadius: '999px',
              border: '1px solid var(--iv-line)'
            }}>
              <button
                type="button"
                onClick={() => { setAuthMode('password'); setAuthError(''); setEmailSuccessMsg(''); }}
                style={{
                  flex: 1,
                  padding: '7px 12px',
                  borderRadius: '999px',
                  border: 'none',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: authMode === 'password' ? 'var(--iv-btn)' : 'transparent',
                  color: authMode === 'password' ? 'var(--iv-btn-fg)' : 'var(--iv-muted)',
                  transition: 'all 0.15s ease'
                }}
              >
                Password
              </button>
              <button
                type="button"
                onClick={() => { setAuthMode('email'); setAuthError(''); setEmailSuccessMsg(''); }}
                style={{
                  flex: 1,
                  padding: '7px 12px',
                  borderRadius: '999px',
                  border: 'none',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: authMode === 'email' ? 'var(--iv-btn)' : 'transparent',
                  color: authMode === 'email' ? 'var(--iv-btn-fg)' : 'var(--iv-muted)',
                  transition: 'all 0.15s ease'
                }}
              >
                Magic Link
              </button>
              {adminAccess.passcodeEnabled && (
              <button
                type="button"
                onClick={() => { setAuthMode('passcode'); setAuthError(''); setEmailSuccessMsg(''); }}
                style={{
                  flex: 1,
                  padding: '7px 12px',
                  borderRadius: '999px',
                  border: 'none',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: authMode === 'passcode' ? 'var(--iv-btn)' : 'transparent',
                  color: authMode === 'passcode' ? 'var(--iv-btn-fg)' : 'var(--iv-muted)',
                  transition: 'all 0.15s ease'
                }}
              >
                Emergency Passcode
              </button>
              )}
            </div>

            {/* Error & Success Messages */}
            {authError && (
              <div style={{
                backgroundColor: 'var(--iv-bg)',
                border: '1px solid var(--iv-danger)',
                color: 'var(--iv-danger)',
                padding: '11px 14px',
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: 600,
                marginBottom: '16px',
                textAlign: 'left',
                lineHeight: 1.45
              }}>
                {authError}
              </div>
            )}

            {emailSuccessMsg && (
              <div style={{
                backgroundColor: 'var(--iv-bg)',
                color: 'var(--iv-body)',
                border: '1px solid var(--iv-line)',
                padding: '12px 14px',
                borderRadius: '10px',
                fontSize: '13.5px',
                lineHeight: 1.45,
                fontWeight: 500,
                marginBottom: '16px',
                textAlign: 'left'
              }}>
                ✓ {emailSuccessMsg}
              </div>
            )}

            {/* Mode 1: Password Sign In */}
            {authMode === 'password' && (
              <form onSubmit={handlePasswordLoginSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ textAlign: 'left' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', marginBottom: '5px' }}>
                    Admin Email
                  </label>
                  <input
                    type="email"
                    required
                    autoFocus
                    placeholder="name@pathplaylabs.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1.5px solid var(--iv-line)',
                      fontSize: '14.5px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      backgroundColor: 'var(--iv-bg)'
                    }}
                  />
                </div>

                <div style={{ textAlign: 'left' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--iv-body)', marginBottom: '5px' }}>
                    Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      placeholder="Enter administrator password..."
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 42px 12px 16px',
                        borderRadius: '10px',
                        border: '1.5px solid var(--iv-line)',
                        fontSize: '14.5px',
                        boxSizing: 'border-box',
                        outline: 'none',
                        backgroundColor: 'var(--iv-bg)'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'var(--iv-muted)',
                        cursor: 'pointer'
                      }}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={authenticating}
                  style={{
                    backgroundColor: 'var(--iv-btn)',
                    color: 'var(--iv-btn-fg)',
                    border: 'none',
                    padding: '13px',
                    borderRadius: '999px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: authenticating ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.15s ease',
                    marginTop: '6px'
                  }}
                >
                  {authenticating ? 'Signing In…' : 'Sign In as Administrator'}
                </button>
              </form>
            )}

            {/* Mode 2: Email Magic Link Form */}
            {authMode === 'email' && (
              <form onSubmit={handleEmailMagicLinkSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ textAlign: 'left' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', marginBottom: '5px' }}>
                    Administrator Email
                  </label>
                  <input
                    type="email"
                    required
                    autoFocus
                    placeholder="name@pathplaylabs.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1.5px solid var(--iv-line)',
                      fontSize: '14.5px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      backgroundColor: 'var(--iv-bg)'
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={emailSending}
                  style={{
                    backgroundColor: 'var(--iv-btn)',
                    color: 'var(--iv-btn-fg)',
                    border: 'none',
                    padding: '13px',
                    borderRadius: '999px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: emailSending ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.15s ease',
                    marginTop: '6px'
                  }}
                >
                  {emailSending ? 'Dispatching Link…' : 'Send Admin Magic Link'}
                </button>
              </form>
            )}

            {/* Mode 3: Emergency Passcode Form */}
            {adminAccess.passcodeEnabled && authMode === 'passcode' && (
              <form onSubmit={handlePasscodeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ textAlign: 'left' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', marginBottom: '5px' }}>
                    Emergency Admin Passcode
                  </label>
                  <input
                    type="password"
                    required
                    autoFocus
                    placeholder="Enter passcode..."
                    value={passcodeInput}
                    onChange={(e) => setPasscodeInput(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1.5px solid var(--iv-line)',
                      fontSize: '15px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      textAlign: 'center',
                      letterSpacing: '2px',
                      backgroundColor: 'var(--iv-bg)'
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={authenticating}
                  style={{
                    backgroundColor: 'var(--iv-btn)',
                    color: 'var(--iv-btn-fg)',
                    border: 'none',
                    padding: '13px',
                    borderRadius: '999px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: authenticating ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.15s ease',
                    marginTop: '6px'
                  }}
                >
                  {authenticating ? 'Authenticating…' : 'Unlock Cert Portal'}
                </button>
              </form>
            )}

          </div>
        </main>
      ) : (
        /* Authenticated CertAdmin Portal */
        <main className="cert-portal-main">
          {/* Header Bar */}
          <div className="cert-portal-header-bar" style={{ borderRadius: '16px', border: '1px solid var(--iv-line)', marginBottom: '28px' }}>
            <div className="cert-portal-brand">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span className="cert-portal-brand-badge">IVORI Edu Credentials</span>
                  <span style={{ fontSize: '12px', color: 'var(--iv-muted)' }}>Issuer: <strong>IVORI Edu</strong></span>
                </div>
                <h1 className="cert-portal-title">Certificate Administrator Portal</h1>
                <p className="cert-portal-subtitle">
                  Signed in as <strong>{user?.email || 'Authorized Administrator'}</strong>
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleLogoutAdmin}
              style={{
                padding: '8px 18px',
                borderRadius: '999px',
                border: '1px solid var(--iv-line)',
                backgroundColor: 'var(--iv-surface)',
                color: 'var(--iv-head)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--iv-bg)'; e.currentTarget.style.borderColor = 'var(--iv-head)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--iv-panel)'; e.currentTarget.style.borderColor = 'var(--iv-line)'; }}
            >
              Sign Out
            </button>
          </div>

          {/* Quick Stats Grid */}
          <div className="cert-portal-stats-grid">
            <div className="cert-portal-stat-card">
              <span className="cert-portal-stat-label">Total Issued Credentials</span>
              <span className="cert-portal-stat-value">{certificatesList.length}</span>
            </div>
            <div className="cert-portal-stat-card">
              <span className="cert-portal-stat-label">Verification Engine Status</span>
              <span className="cert-portal-stat-value" style={{ color: 'var(--iv-muted)', fontSize: '20px' }}>Active (Cryptographic ID)</span>
            </div>
            <div className="cert-portal-stat-card">
              <span className="cert-portal-stat-label">Issuer Standard</span>
              <span className="cert-portal-stat-value" style={{ fontSize: '20px' }}>IVORI Edu (LinkedIn + PDF)</span>
            </div>
            <div className="cert-portal-stat-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <span className="cert-portal-stat-label">Paid User Auto-Issuance</span>
                <span className="cert-portal-stat-value" style={{ fontSize: '15px', color: 'var(--iv-muted)' }}>✓ Active (Auto-Trigger)</span>
              </div>
              <button
                type="button"
                disabled={syncingPaid}
                onClick={handleSyncPaidUsers}
                style={{
                  marginTop: '10px',
                  padding: '7px 14px',
                  borderRadius: '999px',
                  border: '1px solid var(--iv-btn)',
                  backgroundColor: 'var(--iv-btn)',
                  color: 'var(--iv-btn-fg)',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: syncingPaid ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                {syncingPaid ? 'Syncing Paid Users…' : '⚡ Sync All Paid Users'}
              </button>
            </div>
          </div>

          {/* Tab Bar */}
          <div className="cert-portal-tabs">
            <button
              type="button"
              className={`cert-portal-tab-btn ${activeTab === 'issue' ? 'active' : ''}`}
              onClick={() => setActiveTab('issue')}
            >
              Issue New Certificate
            </button>
            <button
              type="button"
              className={`cert-portal-tab-btn ${activeTab === 'registry' ? 'active' : ''}`}
              onClick={() => setActiveTab('registry')}
            >
              Master Registry Ledger ({certificatesList.length})
            </button>
          </div>

          {toastMessage && (
            <div style={{
              background: 'var(--iv-btn)',
              color: 'var(--iv-btn-fg)',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              marginBottom: '20px'
            }}>
              {toastMessage}
            </div>
          )}

          {/* Tab 1: Issue Certificate Studio */}
          {activeTab === 'issue' && (
            <div className="cert-portal-form-card">
              <h2 style={{ fontFamily: 'var(--iv-font-head)', fontSize: '22px', margin: '0 0 8px 0', color: 'var(--iv-head)' }}>
                Issue Authenticated Certificate
              </h2>
              <p style={{ fontSize: '14px', color: 'var(--iv-muted)', margin: '0 0 20px 0' }}>
                Select a certificate category below or customize details to generate a cryptographic verification ID, printable PDF document, and LinkedIn credential code.
              </p>

              {/* Certificate Template Quick-Select Tabs */}
              <div style={{ marginBottom: '24px' }}>
                <label className="cert-portal-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Select Certificate Category / Preset:
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {CERTIFICATE_TYPES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => handleSelectType(t)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '6px',
                        border: selectedTypeId === t.id ? '1.5px solid var(--iv-btn)' : '1px solid var(--iv-line)',
                        background: selectedTypeId === t.id ? 'var(--iv-btn)' : 'var(--iv-surface)',
                        color: selectedTypeId === t.id ? 'var(--iv-btn-fg)' : 'var(--iv-muted)',
                        fontWeight: 600,
                        fontSize: '12.5px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {errorMessage && (
                <div style={{
                  backgroundColor: 'var(--iv-bg)',
                  color: 'var(--iv-danger)',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  marginBottom: '16px'
                }}>
                  {errorMessage}
                </div>
              )}

              {issuedResult && (
                <div className="cert-portal-result-banner">
                  <div>
                    <strong style={{ color: 'var(--iv-body)', fontSize: '15px' }}>✓ Certificate Successfully Issued & Signed</strong>
                    <div style={{ fontSize: '13px', color: 'var(--iv-head)', marginTop: '4px' }}>
                      ID: <code style={{ background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace' }}>{issuedResult.id}</code> — {issuedResult.student_name} ({issuedResult.programme_name})
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => copyVerifyLink(issuedResult.id)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '999px',
                        border: '1px solid var(--iv-line)',
                        background: 'var(--iv-surface)',
                        color: 'var(--iv-body)',
                        fontWeight: 600,
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      Copy Link
                    </button>
                    <a
                      href={withAppBasename(`/verify/${issuedResult.id}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '8px 18px',
                        borderRadius: '999px',
                        background: 'var(--iv-band)',
                        color: 'var(--iv-on-band)',
                        fontWeight: 600,
                        fontSize: '13px',
                        textDecoration: 'none'
                      }}
                    >
                      Open & Verify Certificate
                    </a>
                  </div>
                </div>
              )}

              <form onSubmit={handleIssueCertificate}>
                <div className="cert-portal-form-grid">
                  <div className="cert-portal-input-group">
                    <label className="cert-portal-label">Student Name / Directory Lookup *</label>
                    <input
                      type="text"
                      required
                      placeholder="Type name or email to auto-complete..."
                      value={studentName}
                      onChange={(e) => handleStudentSearch(e.target.value)}
                      className="cert-portal-input"
                      autoComplete="off"
                    />
                    {searchingStudents && (
                      <span style={{ fontSize: '11px', color: 'var(--iv-muted)', marginTop: '2px' }}>Searching directory…</span>
                    )}
                    {studentSuggestions.length > 0 && (
                      <ul className="cert-portal-autocomplete">
                        {studentSuggestions.map((s) => (
                          <li
                            key={s.user_id}
                            onClick={() => handleSelectStudent(s)}
                            className="cert-portal-autocomplete-item"
                          >
                            <strong>{s.first_name || s.last_name ? `${s.first_name} ${s.last_name}`.trim() : s.email}</strong> &lt;{s.email}&gt;
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="cert-portal-input-group">
                    <label className="cert-portal-label">Student Email (Optional)</label>
                    <input
                      type="email"
                      placeholder="student@example.com"
                      value={studentEmail}
                      onChange={(e) => setStudentEmail(e.target.value)}
                      className="cert-portal-input"
                    />
                  </div>

                  <div className="cert-portal-input-group">
                    <label className="cert-portal-label">Programme Title *</label>
                    {programme === 'custom' ? (
                      <input
                        type="text"
                        required
                        placeholder="e.g. Oxford Biomedical Science Admissions Intensive"
                        value={customProgramme}
                        onChange={(e) => setCustomProgramme(e.target.value)}
                        className="cert-portal-input"
                      />
                    ) : (
                      <input
                        type="text"
                        required
                        value={programme}
                        onChange={(e) => setProgramme(e.target.value)}
                        className="cert-portal-input"
                      />
                    )}
                  </div>

                  <div className="cert-portal-input-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <label className="cert-portal-label" style={{ marginBottom: 0 }}>Issue Date *</label>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {[
                          { label: 'July 2026 (WEX)', iso: '2026-07-24', month: 'July 2026' },
                          { label: 'August 2026', iso: '2026-08-28', month: 'August 2026' },
                          { label: 'October 2026', iso: '2026-10-08', month: 'October 2026' }
                        ].map((m) => (
                          <button
                            key={m.label}
                            type="button"
                            onClick={() => {
                              setIssueDate(m.iso);
                              if (description.includes('has successfully completed')) {
                                setDescription((prev) => prev.replace(/completed (in [A-Za-z]+ \d{4}|the)/, `completed in ${m.month} the`));
                              }
                            }}
                            style={{
                              padding: '2px 8px',
                              fontSize: '11px',
                              borderRadius: '4px',
                              border: '1px solid var(--iv-line)',
                              background: issueDate.startsWith(m.iso.substring(0, 7)) ? 'var(--iv-btn)' : 'var(--iv-bg)',
                              color: issueDate.startsWith(m.iso.substring(0, 7)) ? 'var(--iv-btn-fg)' : 'var(--iv-muted)',
                              cursor: 'pointer',
                              fontWeight: 600
                            }}
                          >
                            {m.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="date"
                      required
                      value={issueDate}
                      onChange={(e) => setIssueDate(e.target.value)}
                      className="cert-portal-input"
                    />
                  </div>

                  <div className="cert-portal-input-group">
                    <label className="cert-portal-label">Duration</label>
                    <input
                      type="text"
                      placeholder="e.g. Continuous Membership or Experience Week Intensive"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      className="cert-portal-input"
                    />
                  </div>

                  <div className="cert-portal-input-group">
                    <label className="cert-portal-label">Certificate Serial ID (Optional)</label>
                    <input
                      type="text"
                      placeholder="Leave blank for auto-generation (e.g. IVORI-EXP-IB-...)"
                      value={customId}
                      onChange={(e) => setCustomId(e.target.value)}
                      className="cert-portal-input"
                    />
                    <span style={{ fontSize: '11.5px', color: 'var(--iv-muted)', marginTop: '2px' }}>
                      Optional. Leave blank to auto-generate. Specifying an existing serial ID will safely re-issue and update the record without collision errors.
                    </span>
                  </div>

                  <div className="cert-portal-input-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="cert-portal-label">Attestation Statement / Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="cert-portal-input"
                      rows={2}
                      placeholder="e.g. has successfully completed the IVORI For Education Work Experience Programme..."
                    />
                  </div>

                  <div className="cert-portal-input-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="cert-portal-label">Assessed Competencies & Skills (Comma-separated)</label>
                    <input
                      type="text"
                      placeholder="e.g. Financial Modeling & DCF, M&A Case Simulation, LBO Fundamentals"
                      value={skills}
                      onChange={(e) => setSkills(e.target.value)}
                      className="cert-portal-input"
                    />
                  </div>
                </div>

                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <button
                    type="submit"
                    disabled={issuing}
                    className="cert-portal-submit-btn"
                  >
                    {issuing ? 'Generating & Signing Credential...' : 'Issue Verified Certificate (Issuer: IVORI Edu)'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Tab 2: Master Registry Ledger */}
          {activeTab === 'registry' && (
            <div className="cert-portal-form-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h2 style={{ fontFamily: 'var(--iv-font-head)', fontSize: '22px', margin: '0 0 4px 0', color: 'var(--iv-body)' }}>
                    Master Certificate Registry
                  </h2>
                  <p style={{ fontSize: '13.5px', color: 'var(--iv-muted)', margin: 0 }}>
                    Audited ledger of all issued credentials with issuer IVORI Edu.
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    disabled={syncingPaid}
                    onClick={handleSyncPaidUsers}
                    style={{
                      padding: '7px 14px',
                      borderRadius: '999px',
                      border: '1.5px solid var(--iv-head)',
                      backgroundColor: 'var(--iv-surface)',
                      color: 'var(--iv-body)',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      cursor: syncingPaid ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {syncingPaid ? 'Syncing Paid…' : '⚡ Sync Paid Users'}
                  </button>

                  <button
                    type="button"
                    disabled={syncingExpWeek}
                    onClick={handleSyncExperienceWeek}
                    style={{
                      padding: '7px 14px',
                      borderRadius: '999px',
                      border: '1.5px solid var(--iv-head)',
                      backgroundColor: 'var(--iv-surface)',
                      color: 'var(--iv-body)',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      cursor: syncingExpWeek ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {syncingExpWeek ? 'Syncing Exp Week…' : '⚡ Sync Experience Week (Loizou & Co)'}
                  </button>

                  <button
                    type="button"
                    disabled={dispatchingEmail}
                    onClick={handleSendAllUnsent}
                    style={{
                      padding: '7px 14px',
                      borderRadius: '999px',
                      border: 'none',
                      backgroundColor: 'var(--iv-btn)',
                      color: 'var(--iv-btn-fg)',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      cursor: dispatchingEmail ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {dispatchingEmail ? 'Dispatching…' : '📧 Send All Unsent Emails'}
                  </button>

                  <input
                    type="text"
                    placeholder="Search student, ID, or track..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    style={{
                      padding: '7px 14px',
                      borderRadius: '8px',
                      border: '1px solid var(--iv-line)',
                      fontSize: '13px',
                      width: '200px'
                    }}
                  />
                </div>
              </div>

              {loadingLedger ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--iv-muted)' }}>
                  Loading registry ledger...
                </div>
              ) : filteredCertificates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--iv-muted)' }}>
                  No certificates found matching your query.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="cert-portal-table">
                    <thead>
                      <tr>
                        <th>Credential ID</th>
                        <th>Student Name</th>
                        <th>Programme / Track</th>
                        <th>Issue Date</th>
                        <th>Status</th>
                        <th>Email Delivery</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCertificates.map((c) => (
                        <tr key={c.id}>
                          <td>
                            <code style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--iv-body)', background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', padding: '2px 6px', borderRadius: '4px' }}>
                              {c.id}
                            </code>
                          </td>
                          <td>
                            <strong>{c.student_name}</strong>
                            {c.student_email && (
                              <div style={{ fontSize: '12px', color: 'var(--iv-muted)' }}>{c.student_email}</div>
                            )}
                          </td>
                          <td>{c.programme_name}</td>
                          <td>{c.issue_date}</td>
                          <td>
                            <span style={{
                              padding: '3px 10px',
                              borderRadius: '999px',
                              fontSize: '11px',
                              fontWeight: 600,
                              fontFamily: 'var(--iv-font-head)',
                              backgroundColor: 'color-mix(in srgb, var(--iv-accent) 10%, transparent)',
                              color: 'var(--iv-muted)',
                              border: '1px solid var(--iv-line)'
                            }}>
                              {c.status || 'VALID'}
                            </span>
                          </td>
                          <td>
                            {c.email_sent ? (
                              <span style={{
                                padding: '3px 8px',
                                borderRadius: '999px',
                                fontSize: '11px',
                                fontWeight: 600,
                                backgroundColor: 'var(--iv-bg)',
                                color: 'var(--iv-success)',
                                border: '1px solid var(--iv-line)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                ✓ Sent {c.email_sent_at ? `(${c.email_sent_at.slice(5, 10)})` : ''}
                              </span>
                            ) : (
                              <span style={{
                                padding: '3px 8px',
                                borderRadius: '999px',
                                fontSize: '11px',
                                fontWeight: 500,
                                backgroundColor: 'var(--iv-bg)',
                                color: 'var(--iv-muted)',
                                border: '1px solid var(--iv-line)'
                              }}>
                                Pending
                              </span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <a
                                href={withAppBasename(`/verify/${c.id}`)}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  padding: '5px 12px',
                                  borderRadius: '999px',
                                  border: '1px solid var(--iv-head)',
                                  fontSize: '12px',
                                  color: 'var(--iv-body)',
                                  background: 'var(--iv-surface)',
                                  textDecoration: 'none',
                                  fontWeight: 600
                                }}
                              >
                                View
                              </a>
                              <button
                                type="button"
                                onClick={() => copyVerifyLink(c.id)}
                                style={{
                                  padding: '5px 12px',
                                  borderRadius: '999px',
                                  border: '1px solid var(--iv-line)',
                                  fontSize: '12px',
                                  background: 'var(--iv-bg)',
                                  color: 'var(--iv-muted)',
                                  cursor: 'pointer',
                                  fontWeight: 600
                                }}
                              >
                                Copy Link
                              </button>
                              {c.student_email && (
                                <button
                                  type="button"
                                  disabled={dispatchingEmail}
                                  onClick={() => handleSendTestEmails([c.id])}
                                  style={{
                                    padding: '5px 12px',
                                    borderRadius: '999px',
                                    border: '1px solid var(--iv-btn)',
                                    fontSize: '12px',
                                    background: 'var(--iv-btn)',
                                    color: 'var(--iv-btn-fg)',
                                    cursor: dispatchingEmail ? 'not-allowed' : 'pointer',
                                    fontWeight: 600
                                  }}
                                >
                                  Send Email
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </main>
      )}

      <EduSiteFooter />
    </div>
  );
}
